import React, { useState, useEffect, useRef } from "react";
import Button from "../common/Button.jsx";
import styled from "styled-components";
import useMediaQuery from "@mui/material/useMediaQuery";
import { getCulture, getReleaseBy } from "../../utils/Utils.js";
import { formatDate } from "../common/helpers.js";
import Icon from "../common/Icon.jsx";

const Breadcrumb = styled("div")(({ isInDrawer }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: isInDrawer ? "14px" : "16px",
  gap: "4px",
  fontFamily: "pf_highway_sans_pro_lightRg",
}));

const BreadcrumbLink = styled("button")(({ active, isInDrawer }) => ({
  background: "none",
  border: "none",
  padding: 0,
  margin: 0,
  cursor: "pointer",
  fontWeight: 600,
  color: active ? "#0D0C1C" : "#9897A9",
  fontSize: isInDrawer ? "14px" : "16px",
  fontFamily: "inherit",
}));

const Container = styled("div")(({ isInDrawer, step }) => ({
  padding: step === 2 ? 0 : "0 0 16px 0",
  maxWidth: isInDrawer ? "auto" : "500px",
  margin: "0 auto",
  "> * ": {
    fontFamily: "pf_highway_sans_pro_lightRg",
  },
}));

const SelectAllButton = styled("button")({
  color: "#703EFF",
  fontWeight: 600,
  fontSize: 14,
  background: "none",
  border: "none",
  cursor: "pointer",
  paddingLeft: 0,
  "&:hover": {
    color: "#502CB5",
    textDecoration: "none",
  },
});

const TicketList = styled("div")(({ isInDrawer }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  maxHeight: isInDrawer ? "calc(100vh - 303px)" : "calc(100vh - 318px)",
  minHeight: isInDrawer ? "auto" : "calc(100vh - 560px)",
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: "darkgray #CFD1D1",
}));

const TicketCard = styled("div")(({ status }) => ({
  display: "flex",
  alignItems: "start",
  gap: "8px",
  paddingBottom: "16px",
  borderBottom: "1px solid #EDF1F6",
  background: "#FFFFFF",
  pointerEvents: status === "0" || status === "-1" ? "none" : "auto",
  opacity: status === "0" || status === "-1" ? "0.3" : "1",
}));

const CustomCheckbox = styled("input")(({ status }) => ({
  width: "24px",
  height: "24px",
  cursor: "pointer",
  appearance: "none",
  borderRadius: "4px",
  marginLeft: 0,
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  pointerEvents: status === "0" || status === "-1" ? "none" : "auto",
  opacity: status === "0" || status === "-1" ? "0.3" : "1",
  "&:checked": {
    backgroundImage: "url('/Site/images/icons/order-details/checked.svg')",
  },
  "&:not(:checked)": {
    backgroundImage: "url('/Site/images/icons/order-details/unchecked.svg')",
  },
}));

const Badges = styled("div")({
  display: "flex",
  alignItems: "center",
  verticalAlign: "middle",
  gap: 8,
  marginTop: 4,
  flexWrap: "wrap",
});

const StatusBadge = styled("div")(({ status }) => {
  let backgroundColor = "#EDF1F6";
  let color = "#0D0C1C";

  switch (status) {
    case "released":
      backgroundColor = "#FFFCE7";
      break;
    case "nonRefundable":
      backgroundColor = "#FEE7C4";
      break;
    case "notAvailable":
      backgroundColor = "#EDF1F6";
      break;
    case "readyToRelease":
      backgroundColor = "#FFE861";
      break;
    case "refundAmount":
      backgroundColor = "#FCB041";
      color = "#FFFFFF";
      break;
    default:
      backgroundColor = "#EDF1F6";
      break;
  }

  return {
    padding: "2px 8px",
    backgroundColor,
    color,
    borderRadius: "24px",
    fontSize: "12px",
    fontWeight: 400,
    display: "flex",
    alignItems: "center",
    gap: 4,
  };
});

const TicketDetails = styled("div")({
  flex: 1,
  display: "grid",
  gap: "2px 6px",
  marginLeft: 8,
});

const Label = styled("div")(({ status }) => ({
  color: "#a8abaf",
  fontSize: "14px",
  fontWeight: 400,
  pointerEvents: status === "-1" ? "none" : "auto",
  opacity: status === "-1" ? "0.3" : "1",
  "> span:last-of-type": {
    color: "#0D0C1C",
  },
  "> span:first-of-type": {
    marginRight: 6,
  },
}));

const ButtonContainer = styled("div")(({ isInDrawer }) => ({
  position: isInDrawer ? "absolute" : "sticky",
  bottom: 0,
  left: 0,
  width: "100%",
  backgroundColor: "white",
  boxShadow: isInDrawer
    ? "0 10px 10px rgba(0,0,0,0.05)"
    : "0 0 10px rgba(0,0,0,0)",
  padding: isInDrawer ? "16px" : "8px 0",
  zIndex: 10,
}));

const ReleaseTickets = ({ tickets, callBack }) => {
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [seasonEvents, setSeasonEvents] = useState([]);
  const [selectedSeasonEvents, setSelectedSeasonEvents] = useState([]);
  const [step, setStep] = useState(0);
  const isInDrawer = useMediaQuery("(max-width:899px)");
  const ticketListRef = useRef(null);
  const culture = getCulture();

    useEffect(() => {
    if (ticketListRef.current) {
      ticketListRef.current.scrollTop = 0;
    }
  }, [step]);

  const handleRelease = async () => {
    const selectedIds = tickets
      .filter((ticket) => selectedTickets.includes(ticket.id))
      .map((ticket) => ticket.id);

    if (selectedIds.length === 0) return;

    setIsLoading(true);

    try {
      const response = await fetch(
        `/_api/ticket-management/GetSeasonTicketEvents`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(selectedIds),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch season events");
      }

      const data = await response.json();
      const dataWithIds = data.map((ticket) => ({
        ...ticket,
        compositeId: `${ticket.id}-${ticket.ticketId}`,
      }));
      setSeasonEvents(dataWithIds);
      setStep(1);
    } catch (error) {
      console.error("Error fetching season events:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmationStep = () => {
    const selectedIds = seasonEvents
      .filter((ticket) => selectedSeasonEvents.includes(ticket.compositeId))
      .map((ticket) => ticket.id);

    if (selectedIds.length === 0) return;
    setStep(2);
  };

  const releaseSeasonTickets = async () => {
    const payload = seasonEvents
      .filter(
        (ticket) =>
          selectedSeasonEvents.includes(ticket.compositeId) &&
          ticket?.ticketId &&
          ticket?.id
      )
      .map((ticket) => ({
        TicketId: parseInt(ticket.ticketId),
        EventId: parseInt(ticket.id),
      }));

    if (payload.length === 0) return;

    setIsLoading(true);
    try {
      const response = await fetch("/_api/ticket-management/ReleaseSeats", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ TicketEvents: payload }),
      });
      if (!response.ok) {
        throw new Error("Failed to release seats");
      }
      callBack();
      setStep(0);
      setSelectedTickets([]);
      setSelectedSeasonEvents([]);
      setSeasonEvents([]);
    } catch (error) {
      console.error("Error releasing season tickets:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleTicketSelection = (ticketId) => {
    if (selectedTickets.includes(ticketId)) {
      setSelectedTickets(selectedTickets.filter((id) => id !== ticketId));
    } else {
      setSelectedTickets([...selectedTickets, ticketId]);
    }
  };

  const toggleSeasonTicketsSelection = (compositeId) => {
    // Find the ticket by compositeId to check its releaseState
    const ticket = seasonEvents.find((t) => t.compositeId === compositeId);
    if (!ticket || ticket.releaseState === 0 || ticket.releaseState === -1) {
      // Do nothing if ticket is disabled
      return;
    }
    if (selectedSeasonEvents.includes(compositeId)) {
      setSelectedSeasonEvents(
        selectedSeasonEvents.filter((id) => id !== compositeId)
      );
    } else {
      setSelectedSeasonEvents([...selectedSeasonEvents, compositeId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedTickets.length === tickets.length) {
      setSelectedTickets([]);
    } else {
      setSelectedTickets(tickets.map((t) => t.id));
    }
  };

  const handleSelectAllSeasonTickets = () => {
    const selectableEvents = seasonEvents.filter(
      (t) => t.releaseState !== 0 && t.releaseState !== -1
    );
    if (selectedSeasonEvents.length === selectableEvents.length) {
      setSelectedSeasonEvents([]);
    } else {
      setSelectedSeasonEvents(selectableEvents.map((t) => t.compositeId));
    }
  };

  const returnBadges = (state, releaseByDate, refundAmount) => {
    const badges = [];

    if (state === 0) {
      badges.push(
        <StatusBadge status="released" key="released">
          <Icon kind="tick_black" />
          {releaseseatmodal_resources.ReleaseSeatLabelReleased}
        </StatusBadge>
      );
      badges.push(
        <StatusBadge
          status={refundAmount ? "refundAmount" : "nonRefundable"}
          key="refund-status"
        >
          {refundAmount
            ? `${releaseseatmodal_resources.RefundAmount.replace(
                "{0}",
                refundAmount.toFixed(2)
              )}€`
            : releaseseatmodal_resources.NonRefundable}
        </StatusBadge>
      );
    }

    if (state === -1) {
      badges.push(
        <StatusBadge status="notAvailable" key="notAvailable">
          {releaseseatmodal_resources.ReleaseSeatLabelUnavailable}
        </StatusBadge>
      );
    }

    if (state === 1) {
      badges.push(
        <StatusBadge status="readyToRelease" key="readyToRelease">
          {releaseseatmodal_resources.ReleaseSeatLabelAvailable.replace(
            "{0}",
            releaseByDate.releaseBy
          )}
        </StatusBadge>
      );
      badges.push(
        <StatusBadge
          status={refundAmount ? "refundAmount" : "nonRefundable"}
          key="refund-status"
        >
          {refundAmount
            ? `${releaseseatmodal_resources.RefundAmount.replace(
                "{0}",
                refundAmount.toFixed(2)
              )}€`
            : releaseseatmodal_resources.NonRefundable}
        </StatusBadge>
      );
    }

    return badges.length > 0 ? badges : null;
  };

  return (
    <>
      <div>
        <Container id="rt-multi-container" isInDrawer={isInDrawer} step={step}>
          {step !== 2 && (
              <Breadcrumb isInDrawer={isInDrawer}>
                <BreadcrumbLink active={step === 0} isInDrawer={isInDrawer} onClick={() => setStep(0)}>
                {releaseseatmodal_resources.ReleaseSeatStep_ticketsText}
              </BreadcrumbLink>
              <Icon kind="greater" />
              <BreadcrumbLink active={step === 1} isInDrawer={isInDrawer}>
                {releaseseatmodal_resources.ReleaseSeatStep_eventsText}
              </BreadcrumbLink>
            </Breadcrumb>
          )}

          {step === 0 ? (
            <>
              {tickets.length > 1 && (
                <SelectAllButton
                  id="selectAllTickets"
                  onClick={handleSelectAll}
                >
                  {selectedTickets.length === tickets.length
                    ? orderdetails_resources.DeselectAll
                    : orderdetails_resources.SelectAll}
                </SelectAllButton>
              )}

              <TicketList
                isInDrawer={isInDrawer}
                ref={ticketListRef}
                style={{ paddingBottom: isInDrawer ? 96 : 0, marginTop: 16 }}
              >
                {tickets.map((ticket) => (
                  <TicketCard key={ticket.id}>
                    <div>
                      <CustomCheckbox
                        type="checkbox"
                        checked={selectedTickets.includes(ticket.id)}
                        onChange={() => toggleTicketSelection(ticket.id)}
                      />
                    </div>
                    <TicketDetails>
                      <Label id={`rt-title-${ticket.id}`}>
                        <span style={{ fontWeight: "600" }}>
                          {ticket.title}
                        </span>
                      </Label>
                      <Label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <Icon kind="date" />
                        <span id={`rt-date-${ticket.id}`}>
                          {formatDate({
                            date: ticket.eventDateTime,
                            culture,
                            customDate: ticket.customDate,
                          })}
                        </span>
                      </Label>
                      <Label id={`rt-holder-name-${ticket.id}`}>
                        <span>{orderdetails_resources.NameSurname}</span>
                        <span>{ticket.nameSurname}</span>
                      </Label>
                      <Label id={`rt-holder-section-${ticket.id}`}>
                        <span>{orderdetails_resources.Section}</span>
                        <span>{ticket.section}</span>
                      </Label>
                      <Label id={`rt-holder-rowseat-${ticket.id}`}>
                        <span>{orderdetails_resources.RowSeat}</span>
                        <span>
                          {ticket.rowSeat === "Unnumbered"
                            ? orderdetails_resources.Unnumbered
                            : ticket.rowSeat}
                        </span>
                      </Label>
                    </TicketDetails>
                  </TicketCard>
                ))}
              </TicketList>
            </>
          ) : step === 1 ? (
            <>
              {seasonEvents.length > 1 &&
                seasonEvents.filter(
                  (t) => t.releaseState !== 0 && t.releaseState !== -1
                ).length > 0 && (
                  <SelectAllButton
                    id="selectAllTickets"
                    onClick={handleSelectAllSeasonTickets}
                  >
                    {selectedSeasonEvents.length ===
                    seasonEvents.filter(
                      (t) => t.releaseState !== 0 && t.releaseState !== -1
                    ).length
                      ? orderdetails_resources.DeselectAll
                      : orderdetails_resources.SelectAll}
                  </SelectAllButton>
                )}

              <TicketList
                isInDrawer={isInDrawer}
                ref={ticketListRef}
                style={{ paddingBottom: isInDrawer ? 96 : 0, marginTop: 16 }}
              >
                {seasonEvents.map((ticket) => (
                  <TicketCard key={ticket.compositeId}>
                    <div>
                      <CustomCheckbox
                        type="checkbox"
                        checked={selectedSeasonEvents.includes(
                          ticket.compositeId
                        )}
                        onChange={() =>
                          toggleSeasonTicketsSelection(ticket.compositeId)
                        }
                        status={ticket.releaseState.toString()}
                      />
                    </div>
                    <TicketDetails>
                      <Label id={`rt-title-${ticket.id}`} status={ticket.releaseState.toString()}>
                        <span style={{ fontWeight: "600" }}>
                          {ticket.title}
                        </span>
                      </Label>
                      <Label
                        status={ticket.releaseState.toString()}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <Icon kind="date" />
                        <span id="ReleaseTicketDate">
                          {formatDate({
                            date: ticket.eventDateTime,
                            culture,
                            hasWeekday: true,
                          })}
                        </span>
                      </Label>
                      <Label id={`rt-holder-name-${ticket.id}`} status={ticket.releaseState.toString()}>
                        <span>{orderdetails_resources.NameSurname}</span>
                        <span>{ticket.fullName}</span>
                      </Label>
                      <Label id={`rt-holder-section-${ticket.id}`} status={ticket.releaseState.toString()}>
                        <span>{orderdetails_resources.Section}</span>
                        <span>{ticket.section}</span>
                      </Label>
                      <Label id={`rt-holder-rowseat-${ticket.id}`} status={ticket.releaseState.toString()}>
                        <span>{orderdetails_resources.RowSeat}</span>
                        <span>
                          {ticket.seat === "No numbering"
                            ? orderdetails_resources.Unnumbered
                            : ticket.seat}
                        </span>
                      </Label>
                      <Badges>
                        {returnBadges(
                          ticket.releaseState,
                          getReleaseBy(ticket.releaseByDate),
                          ticket.refundAmount
                        )}
                      </Badges>
                    </TicketDetails>
                  </TicketCard>
                ))}
              </TicketList>
            </>
          ) : (
            <div style={{ textAlign: "left" }}>
              <p
                style={{
                  fontSize: "16px",
                  marginBottom: "8px",
                  fontFamily: "inherit",
                }}
              >
                {seasonEvents.some(
                  (ticket) =>
                    selectedSeasonEvents.includes(ticket.compositeId) &&
                    ticket?.ticketId &&
                    ticket?.id &&
                    ticket?.refundAmount > 0 &&
                    !ticket?.shouldRefundAfterSale
                )
                  ? releaseseatmodal_resources.ReleaseSeatConfirmationWithRefund
                  : releaseseatmodal_resources.ReleaseSeatConfirmation}
              </p>
              {seasonEvents.some(
                (ticket) =>
                  selectedSeasonEvents.includes(ticket.compositeId) &&
                  ticket?.ticketId &&
                  ticket?.id &&
                  ticket?.refundAmount > 0
              ) ? (
                <>
                  <p
                    style={{
                      fontSize: "16px",
                      marginBottom: "4px",
                      fontFamily: "inherit",
                    }}
                  >
                    <b>
                      {releaseseatmodal_resources.RefundAmount.replace(
                        "{0}",
                        seasonEvents
                          .filter(
                            (ticket) =>
                              selectedSeasonEvents.includes(
                                ticket.compositeId
                              ) &&
                              ticket?.ticketId &&
                              ticket?.id &&
                              ticket?.refundAmount != null
                          )
                          .reduce((sum, ticket) => sum + ticket.refundAmount, 0)
                          .toFixed(2)
                      )}
                      €
                    </b>
                  </p>
                  {seasonEvents.some(
                    (ticket) =>
                        selectedSeasonEvents.includes(ticket.compositeId) &&
                        ticket?.ticketId &&
                        ticket?.id &&
                        ticket?.refundAmount > 0 &&
                        !ticket?.shouldRefundAfterSale
                    ) ? (
                      <p
                        style={{
                          fontSize: "16px",
                          marginBottom: "24px",
                          fontFamily: "inherit",
                        }}
                      >
                        {releaseseatmodal_resources.RefundAmountInfo1}{" "}<b>{releaseseatmodal_resources.RefundAmountInfo2}</b>
                      </p>
                      ) : (
                      <>
                          <p style={{ fontSize: "16px", marginBottom: "4px", fontFamily: "inherit" }}>
                              {releaseseatmodal_resources.RefundAmountInfo3}{" "}
                              <b>{releaseseatmodal_resources.RefundAmountInfo4}</b>{" "}
                              {releaseseatmodal_resources.RefundAmountInfo5}
                          </p>
                          <p style={{ fontSize: "16px", marginBottom: "24px", fontFamily: "inherit" }}>
                              {releaseseatmodal_resources.RefundAmountInfo6}
                          </p>
                      </>
                    )
                }
                </>
              ) : (
                <p
                  style={{
                    fontSize: "16px",
                    marginBottom: "24px",
                    fontFamily: "inherit",
                  }}
                >
                  {releaseseatmodal_resources.NoRefundAmountInfo1}{" "}
                  <b>{releaseseatmodal_resources.NoRefundAmountInfo2}</b>
                </p>
              )}
              <div
                style={{
                  display: "flex",
                  gap: "24px",
                }}
              >
                <Button
                  type="secondary"
                  text={releaseseatmodal_resources.ReleaseSeatConfirmationNo}
                  fullWidth
                  onClick={() => setStep(1)}
                  disabled={isLoading}
                  height="48px"
                />
                <Button
                  type="primary"
                  text={releaseseatmodal_resources.ReleaseSeatConfirmationYes}
                  fullWidth
                  disabled={isLoading}
                  onClick={() => releaseSeasonTickets()}
                  height="48px"
                />
              </div>
            </div>
          )}
        </Container>
      </div>
      {step !== 2 && (
        <ButtonContainer isInDrawer={isInDrawer}>
          <Button
            type="primary"
            fullWidth
            height={"48px"}
            text={
              step === 0
                ? releaseseatmodal_resources.ReleaseSeatButton_tickets
                : releaseseatmodal_resources.ReleaseSeatButton_events
            }
            onClick={step === 0 ? handleRelease : handleConfirmationStep}
            disabled={
                ((selectedSeasonEvents.length === 0 || isLoading) && step === 1) || (selectedTickets.length === 0 && step === 0)
            }
          />
        </ButtonContainer>
      )}
    </>
  );
};
export default ReleaseTickets;
